import { useMemo } from 'react'
import type { RangeKey } from '@dataTypes/dashboard.types'
import {
  getProductionByProvider,
  getProductionByProviderOverview,
} from '@services/api/dashboard/finance.api'
import type { ProductionByProviderRow } from '@services/api/dashboard/finance.api'
import {
  formatCurrency,
  financeProviderChartLabel,
} from '@utils/dashboardFinance.util'
import {
  useDashboardPeriodFetch,
  useDashboardPeriodParams,
  type DashboardPeriodFetcher,
} from './useDashboardPeriod'
import { hasNonZeroDashboardValues } from '@utils/dashboardSectionEmpty.util'

export type UseFinanceProductionPerProviderOptions = {
  range: RangeKey
  date: string
  providerSearch: string
  enabled?: boolean
  queued?: boolean
}

type ProviderBarChartRow = {
  label: string
  value: number
}

export type UseFinanceProductionPerProviderResult = {
  loading: boolean
  error: string | null
  /** Response has arrived (`data != null`); independent of nonzero content. */
  hasPayload: boolean
  hasData: boolean
  providerRows: ProductionByProviderRow[]
  providerTotalFormatted: string
  filteredProviderData: ProductionByProviderRow[]
  providerBarChartData: ProviderBarChartRow[]
}

function useFinanceProductionPerProviderFromFetcher({
  range,
  date,
  providerSearch,
  enabled = true,
  queued = false,
  fetcher,
}: UseFinanceProductionPerProviderOptions & {
  fetcher: DashboardPeriodFetcher<
    Awaited<ReturnType<typeof getProductionByProvider>>
  >
}): UseFinanceProductionPerProviderResult {
  const queryParams = useDashboardPeriodParams(range, date)
  const { data, loading, error } = useDashboardPeriodFetch(
    queryParams,
    fetcher,
    { enabled, queued },
  )

  const providerRows: ProductionByProviderRow[] = useMemo(
    () => data?.rows ?? [],
    [data],
  )
  const providerTotal = useMemo(
    () =>
      providerRows.reduce((sum, row) => sum + (row.total_production ?? 0), 0),
    [providerRows],
  )
  const providerTotalFormatted = useMemo(
    () => formatCurrency(providerTotal),
    [providerTotal],
  )

  const filteredProviderData = useMemo(() => {
    if (!providerSearch.trim()) return providerRows
    const q = providerSearch.toLowerCase()
    return providerRows.filter(
      (row) =>
        row.provider.toLowerCase().includes(q) ||
        (row.credential ?? '').toLowerCase().includes(q),
    )
  }, [providerRows, providerSearch])

  const providerBarChartData = useMemo(
    () =>
      filteredProviderData.map((row) => ({
        label: financeProviderChartLabel(row.provider),
        value: row.total_production,
      })),
    [filteredProviderData],
  )

  return {
    loading,
    error,
    hasPayload: data != null,
    hasData: providerRows.some((row) =>
      hasNonZeroDashboardValues(row.total_production, row.total_procedures),
    ),
    providerRows,
    providerTotalFormatted,
    filteredProviderData,
    providerBarChartData,
  }
}

export function useFinanceProductionPerProvider(
  options: UseFinanceProductionPerProviderOptions,
): UseFinanceProductionPerProviderResult {
  return useFinanceProductionPerProviderFromFetcher({
    ...options,
    fetcher: getProductionByProvider,
  })
}

export function useFinanceProductionPerProviderOverview(
  options: UseFinanceProductionPerProviderOptions,
): UseFinanceProductionPerProviderResult {
  return useFinanceProductionPerProviderFromFetcher({
    ...options,
    fetcher: getProductionByProviderOverview,
  })
}
